// API Configuration - matches mobile app's api_config.dart
export const API_CONFIG = {
  baseUrl: 'https://api.vivideo.ai',
  
  // Endpoints
  endpoints: {
    getUserById: '/get-user-id',
    getUserInfo: '/get-user-info',
    // Passwordless email login: send a 6-digit code, then verify it (same lambda
    // the mobile apps use). On verify, we get-or-create the user via getUserById.
    emailOtp: '/email-otp',
    updateUserInfo: '/update-user-info',
    createFolder: '/create-folder',
    getFolders: '/get-folders',
    folderOperation: '/folder-operation',
    getFiles: '/get-files',
    fileOperation: '/file-operation',
    fileUpload: '/file-upload',
    createOrder: '/create-order',
    createCheckoutSession: '/create-checkout-session',
    activateTrial: '/activate-trial',
    stripeWebhook: '/stripe-webhook',
    uploadHeygenAsset: '/upload-heygen-asset',
    // V2-only copy of upload-heygen-asset that ALSO accepts PDFs (additive; V1 untouched).
    v2UploadHeygenAsset: '/v2-upload-heygen-asset',
    manageSubscription: '/manage-subscription',
    // V2 self-service cancellation funnel (Settings → Manage Subscription):
    // subscription info + downgrade offer, churn survey, retention coupon and
    // the real Stripe cancel handoff. Additive — /manage-subscription untouched.
    v2CancelSubscription: '/v2-cancel-subscription',
    // Recovery/exit offer: server-owned 10-min deadline for the one-time
    // $4.99-first-week Pro weekly offer shown when a free user leaves the paywall
    // without ever starting a checkout (actions: start | status).
    v2ExitOffer: '/v2-exit-offer',
    // Recovery-email offers ($4.99 / $1.99 first week): read-only status of the
    // server-owned offer + deadline stamped by the email sender (action: status).
    v2EmailOffer: '/v2-email-offer',
    // Trade a recovery-email login ticket (the ?lt= param) for a real session
    // token, so email CTAs land on the in-app paywall already signed in.
    emailLoginExchange: '/email-login-exchange',
    directPayment: '/direct-payment',
    // Auto-reload credits (paid users): settings + the server-authoritative
    // below-threshold check that charges the default card and tops up.
    v2AutoReload: '/v2-auto-reload',
    deleteUser: '/delete-user',
    // v2 HeyGen chat (mode=chat, /v3/video-agents) — additive, does not touch v1 flow
    heygenChatCreate: '/heygen-chat-create',
    heygenChatMessage: '/heygen-chat-message',
    heygenChatSession: '/heygen-chat-session',
    heygenChatResource: '/heygen-chat-resource',
    heygenChatStop: '/heygen-chat-stop',
    // Rewards / review submissions (free-plan users)
    getReviewUploadUrl: '/get-review-upload-url',
    submitReview: '/submit-review',
    listUserSubmissions: '/list-user-submissions',
    // Enterprise contact form (sends email to support@vivideo.ai via Resend)
    enterpriseContact: '/enterprise-contact',

    // ===== V2 (Assets + agent generation). Additive — not used by the live v1 UI. =====
    // Reads (public catalogues + the user's owned assets):
    v2ListVoices: '/v2-list-voices',
    v2ListAvatars: '/v2-list-avatars',
    v2ListStyles: '/v2-list-styles',
    // Templates v2 — curated video PRESETS gallery (prompt + model + duration +
    // preview). Separate from v2-list-styles (HeyGen agent styles). Read-only.
    v2ListTemplates: '/v2-list-templates',
    // Avatar writes:
    v2CreateAvatar: '/v2-create-avatar',
    v2AvatarStatus: '/v2-avatar-status',
    v2CreateAvatarConsent: '/v2-create-avatar-consent',
    // Voice writes:
    v2CloneVoice: '/v2-clone-voice',
    v2VoiceStatus: '/v2-voice-status',
    v2DesignVoice: '/v2-design-voice',
    // Chat messages/approval with the server-side FREE-TIER gate:
    v2HeygenChatMessage: '/v2-heygen-chat-message',
    // TTS preview — "hear this voice say my line":
    v2VoiceSpeech: '/v2-voice-speech',
    // One-time retention credits for the delete-account funnel (free users too):
    v2GrantCredits: '/v2-grant-credits',
    // Refund credits for agent renders stuck in_progress (HeyGen died, no webhook):
    v2HeygenReconcile: '/v2-heygen-reconcile',
    // NEW chat brain — Vivideo's own Claude planner (stateful: stores convo + plan
    // on the order). Empty message = load/poll. Never touches HeyGen.
    v2AgentChat: '/v2-agent-chat',
    // Generate gate — render a finished plan via HeyGen (the only first-render trigger).
    v2HeygenGenerate: '/v2-heygen-generate',
    // LongVid twins of the two endpoints above — same request/response shapes and
    // the same actions. Which pair a call uses is the user's (new project) or the
    // order's (existing project) video provider; see api/providerEndpoints.ts.
    v2LongvidChat: '/v2-longvid-chat',
    v2LongvidGenerate: '/v2-longvid-generate',
    // VideoGen twins — the same request/response shapes and actions again, for the
    // third provider group. Routed exactly like the LongVid pair above.
    v2VideogenChat: '/v2-videogen-chat',
    v2VideogenGenerate: '/v2-videogen-generate',
    // Generate a cheap fal cover image for a plan (HeyGen exposes no cover URL):
    v2PlanCover: '/v2-plan-cover',
    // Live fal queue progress for a manual order (real loading state):
    v2FalStatus: '/v2-fal-status',
    // Rename/delete the user's OWN avatars:
    v2ManageAvatar: '/v2-manage-avatar',
    // Per-user asset library (presigned upload / complete / list / delete):
    v2Assets: '/v2-assets',
    // Per-user brand kits (reverse-engineered HeyGen brand system):
    v2Brands: '/v2-brands',
    // URL → brand kit: explore a site, extract logo/name/palette/fonts/identity
    // and pre-fill the brand-kit form (the user reviews before creating):
    v2BrandFromUrl: '/v2-brand-from-url',
    // Manual-mode orders for the new fal.ai models (per-model payload schemas);
    // completion + refunds ride the live fal-webhook:
    v2CreateOrder: '/v2-create-order',
    // PUBLIC-API key management (Settings → More → API Keys). The keys themselves
    // authenticate the separate public /v1/* API — see https://developers.vivideo.ai
    v2ApiKeys: '/v2-api-keys',

    // ===== Team workspaces (V2 additive) =====
    // Org CRUD + memberships/roles + usage aggregation:
    v2Org: '/v2-org',
    // Email invites (send/list/revoke/accept/pending-for-me):
    v2OrgInvite: '/v2-org-invite',
    // Team Stripe billing (checkout / plan updates / pooled top-ups / portal):
    v2OrgBilling: '/v2-org-billing',
  },
} as const;

// ===== Team plan pricing (mirrors v2-org-billing's TEAM_AMOUNTS — cents there, dollars here) =====
export const TEAM_PLAN = {
  includedSeats: 2,
  maxSeats: 50,
  baseCredits: 200,
  creditTiers: [200, 400, 600, 800, 1000],
  topUpMinCredits: 500,
  weekly: {
    base: 40,
    seat: 15,
    // TOTAL weekly price per credit tier (base included)
    creditTierTotals: { 200: 40, 400: 76, 600: 110, 800: 142, 1000: 170 } as Record<number, number>,
  },
  yearly: {
    // charged yearly; shown as per-week equivalents
    base: 520,
    seat: 260,
    basePerWeek: 10,
    seatPerWeek: 5,
    creditTierTotals: { 200: 520, 400: 988, 600: 1456, 800: 1872, 1000: 2236 } as Record<number, number>,
    creditTierTotalsPerWeek: { 200: 10, 400: 19, 600: 28, 800: 36, 1000: 43 } as Record<number, number>,
  },
} as const;

/** Volume-discount % vs linear pricing for a credit tier (shown to encourage upsizing). */
export function teamCreditTierSavings(credits: number, billing: 'weekly' | 'yearly'): number {
  if (credits <= TEAM_PLAN.baseCredits) return 0;
  const totals = billing === 'weekly' ? TEAM_PLAN.weekly.creditTierTotals : TEAM_PLAN.yearly.creditTierTotals;
  const base = billing === 'weekly' ? TEAM_PLAN.weekly.base : TEAM_PLAN.yearly.base;
  const linear = base * (credits / TEAM_PLAN.baseCredits);
  return Math.round((1 - totals[credits] / linear) * 100);
}

// Request parameter names
export const API_PARAMS = {
  userId: 'user_id',
  userEmail: 'user_email',
  firebaseUid: 'firebase_uid',
  platform: 'platform',
  folderId: 'folder_id',
  folderName: 'folder_name',
  orderId: 'order_id',
  orderType: 'order_type',
  orderData: 'order_data',
  orderName: 'order_name',
  operation: 'operation',
  newName: 'new_name',
  targetFolderId: 'target_folder_id',
  fileName: 'file_name',
  fileType: 'file_type',
  priceId: 'price_id',
} as const;

// Response field names
export const API_RESPONSE = {
  userId: 'user_id',
  orderId: 'order_id',
  folderId: 'folder_id',
  uploadUrl: 'upload_url',
  publicUrl: 'public_url',
  checkoutUrl: 'checkout_url',
  error: 'error',
  message: 'message',
} as const;

// Credit cost per order (default)
export const CREDIT_COST = 10;

// Credit costs per operation type
export const CREDIT_COSTS: Record<string, number> = {
  text_to_video: 10,
  image_to_video: 10,
  video_remix: 10,  // Video remix using Sora 2
  video_extend: 10, // Extend video using Veo 3.1
  video_edit_wan: 10, // Edit video using Wan
  first_frame_last_frame: 10,
  video_to_video: 10,
  video_to_audio: 10,
  remove_video_background: 10,
  upscale_video: 10,
  lipsync: 10,
  auto_caption: 10,
  dubbing: 10,
  ai_avatar: 10,
  pixverse_effects: 10,
  effects: 10,
  reframe: 10,
  reframe_aspect_ratio: 10,
  reference_to_video: 10,
  multiple_images: 25, // Multiple images costs more
};

// Subscription prices - LIVE Stripe Price IDs (legacy - used for analytics tracking)
export const SUBSCRIPTION_PRICES = {
  weekly: {
    id: 'price_1SmGFNFSV33rh8Z2A37KGyoA',
    productName: 'Vivideo Pro Weekly',
    amount: 19.99,
    introAmount: 9.99, // 50% off first week
    credits: 50,
    label: 'Weekly',
    period: '/week',
    trialDays: 7, // 7-day intro period at discounted rate
    discountPercent: 50,
  },
} as const;

// Subscription Plans
export const PRO_PLAN = {
  weeklyPriceId: 'price_1SpTICFSV33rh8Z2AwZekmEl',
  monthlyPriceId: 'price_1TL4tBFSV33rh8Z2PrTkscBQ',
  yearlyPriceId: 'price_1SxRu8FSV33rh8Z21vi5EH0i',
  weeklyAmount: 9,
  monthlyAmount: 20,
  monthlyPerWeek: 5,
  monthlySavingsPercent: 44,
  yearlyAmount: 99,
  yearlyPerWeek: 2,
  yearlySavings: 369,
  yearlySavingsPercent: 75,
  credits: 30,
  // Recovery/exit offer: discounted first week (then weeklyAmount thereafter).
  exitFirstWeekAmount: 4.99,
} as const;

export const MAX_PLAN = {
  weeklyPriceId: 'price_1T2Y6nFSV33rh8Z2cG1AXXaZ',
  monthlyPriceId: 'price_1TL4tBFSV33rh8Z2VQoQEocG',
  yearlyPriceId: 'price_1T2Y9YFSV33rh8Z2XcUCCKOW',
  weeklyAmount: 19,
  monthlyAmount: 40,
  monthlyPerWeek: 10,
  monthlySavingsPercent: 47,
  yearlyAmount: 199,
  yearlyPerWeek: 4,
  yearlySavings: 789,
  yearlySavingsPercent: 79,
  credits: 100,
} as const;
